import { useEffect, useId, useRef, useState } from "react";
import { theme, card, floatingSheet, btnPrimary, inputStyle } from "../theme";
import { RulerProgress } from "../components/Drawing";
import { LoadingBlock } from "../components/Spinner";
import { api, apiErrorMessage } from "../api/client";
import { fmtDateTime } from "../lib/format";
import { useCurrentUser } from "../auth/useCurrentUser";
import { useFrameworks } from "../context/FrameworksContext";
import { DocumentTemplatePanel } from "../components/DocumentTemplatePanel";
import { ConsultantDocGenPanel } from "../components/ConsultantDocGenPanel";
import { ModelPicker } from "../components/ModelPicker";
import { CI_SECTORS, CI_SECTOR_PARENTS, subSectors } from "../data/sectorsData";
import { cbPalette, cbSafe } from "../lib/colourBlind";
// The one frontend copy of country → laws, which the backend sync test compares with lib/jurisdictions.ts.
import { regulationsForCountries } from "../data/countriesData";
import { US_STATES } from "../data/usStates";

// ── Country list ──────────────────────────────────────────────────────────

const COUNTRIES = [
  "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda",
  "Argentina","Armenia","Australia","Austria","Azerbaijan","Bahamas","Bahrain",
  "Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan","Bolivia",
  "Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso",
  "Burundi","Cabo Verde","Cambodia","Cameroon","Canada","Central African Republic",
  "Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica","Croatia","Cuba",
  "Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic",
  "Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini",
  "Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana",
  "Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guernsey","Guyana","Haiti",
  "Honduras","Hong Kong","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland",
  "Isle of Man","Israel","Italy","Jamaica","Japan","Jersey","Jordan","Kazakhstan","Kenya",
  "Kiribati","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya",
  "Liechtenstein","Lithuania","Luxembourg","Madagascar","Malawi","Malaysia","Maldives",
  "Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia",
  "Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia",
  "Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Korea",
  "North Macedonia","Norway","Oman","Pakistan","Palau","Palestine","Panama",
  "Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania",
  "Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines",
  "Samoa","San Marino","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone",
  "Singapore","Slovakia","Slovenia","Solomon Islands","Somalia","South Africa",
  "South Korea","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland",
  "Syria","Taiwan","Tajikistan","Tanzania","Thailand","Timor-Leste","Togo","Tonga",
  "Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine",
  "United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu",
  "Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe",
].sort();


// ── Types ─────────────────────────────────────────────────────────────────

/** Just enough of a member to suggest them for a named responsibility. */
interface Member {
  id: string;
  displayName: string;
  jobTitle: string | null;
}

interface Organization {
  id: string;
  name: string;
  sector: string | null;
  sectorCode: string | null;
  size: string | null;
  /** Legal Entity Identifier (ISO 17442) — DORA Register of Information RT.01.01. */
  lei?: string | null;
  doraMonitoringEnabled?: boolean;
  geography: string | null;        // JSON string: string[]
  regulations: string | null;      // JSON string: string[]
  ismsScope: string | null;
  aimsScope: string | null;
  qmsScope: string | null;
  socServiceDescription: string | null;
  riskAppetite: string | null;
  reviewFrequency: string | null;
  effectiveDate: string | null;
  ceo: string | null;
  ciso: string | null;
  dpo: string | null;
  dpoNotifiedToAuthorityAt?: string | null;
  itManager: string | null;
  hrLead: string | null;
  chiefAiOfficer: string | null;
  qualityManager: string | null;
  infrastructure: string | null;
  cloudProviders: string | null;
  coreApps: string | null;
  idp: string | null;
  siem: string | null;
  vulnScanner: string | null;
  backup: string | null;
  dpTier: string | null;
  // Brand identity
  logoBlobPath: string | null;
  primaryBrandColor: string | null;
  accentBrandColor: string | null;
  useBrandColorsInPolicies: boolean | null;
  [key: string]: string | boolean | null | undefined;
}

// ── Jersey DPJL 2018 tier ─────────────────────────────────────────────────

export const DP_TIERS: { value: string; label: string; description: string }[] = [
  { value: "sole_trader", label: "Sole Trader",   description: "0 employees — no FTEs beyond the owner" },
  { value: "small",       label: "Small",          description: "1–9 FTEs" },
  { value: "medium",      label: "Medium",         description: "10–49 FTEs" },
  { value: "large",       label: "Large",          description: "50+ FTEs" },
  { value: "charity",     label: "Charity / Non-profit", description: "Exempt from registration fees; set by consultant" },
];

export const DP_TIER_COLORS: Record<string, { bg: string; text: string }> = cbPalette({
  sole_trader: { bg: "#f3f4f6", text: "#6b7280" },
  small:       { bg: "#dbeafe", text: "#1d4ed8" },
  medium:      { bg: "#e0e7ff", text: "#4338ca" },
  large:       { bg: "#fef3c7", text: "#b45309" },
  charity:     { bg: "#d1fae5", text: "#065f46" },
});

export function DpTierBadge({ tier }: { tier: string | null }) {
  if (!tier) return null;
  const t = DP_TIERS.find((x) => x.value === tier);
  if (!t) return null;
  const c = DP_TIER_COLORS[tier] ?? { bg: "#f3f4f6", text: "#374151" };
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 3,
      fontSize: 11,
      fontWeight: 600,
      background: c.bg,
      color: c.text,
    }}>
      {t.label}
    </span>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────

function parseJsonArray(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    // Legacy free-text: treat as a single item so data isn't silently lost
    return raw.trim() ? [raw.trim()] : [];
  }
}

// ── Multi-select country picker ───────────────────────────────────────────

export function CountryPicker({
  selected,
  onChange,
  locked = [],
  lockedHint = "Set by a site — remove or move the site to drop it",
  options = COUNTRIES,
  placeholder = "Search and add countries…",
}: {
  selected: string[];
  onChange: (v: string[]) => void;
  /** Countries that cannot be removed here (they come from the organisation's sites). */
  locked?: string[];
  lockedHint?: string;
  /** What can be picked: the countries, or the US states. */
  options?: readonly string[];
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = options.filter(
    (c) => !selected.includes(c) && c.toLowerCase().includes(query.toLowerCase())
  ).slice(0, 40);

  function add(country: string) {
    onChange([...selected, country]);
    setQuery("");
  }

  function remove(country: string) {
    if (locked.includes(country)) return;
    onChange(selected.filter((c) => c !== country));
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Selected chips */}
      {selected.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
          {selected.map((c) => (
            <span
              key={c}
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: theme.color.accentBg, border: `1px solid ${theme.color.border}`,
                borderRadius: 3, padding: "3px 10px", fontSize: 12, color: theme.color.accentText,
              }}
            >
              {c}
              {locked.includes(c) ? (
                <span title={lockedHint} style={{ color: theme.color.faint, fontSize: 10, lineHeight: 1 }}>site</span>
              ) : (
                <button
                  onClick={() => remove(c)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.faint, padding: 0, lineHeight: 1, fontSize: 13 }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {/* Search input */}
      <input
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
      />
      {/* Dropdown */}
      {open && filtered.length > 0 && (
        <div style={{
          ...floatingSheet,
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100,
          marginTop: 2, maxHeight: 220, overflowY: "auto",
        }}>
          {filtered.map((c) => (
            <div
              key={c}
              onMouseDown={(e) => { e.preventDefault(); add(c); }}
              style={{
                padding: "8px 12px", fontSize: 13, cursor: "pointer", color: theme.color.text,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = theme.color.accentBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Regulations display (read-only — auto-derived from geography on save) ──

const CYBERSECURITY_STANDARDS = new Set([
  "NIS2 Directive",
  "EU Cybersecurity Act 2019 (CSA)",
  "EU Cyber Resilience Act 2024 (CRA)",
]);

function RegChip({ label, isStandard }: { label: string; isStandard: boolean }) {
  const bg     = isStandard ? "var(--c-panelAlt)"  : theme.color.accentBg;
  const color  = isStandard ? theme.color.heading   : theme.color.accentText;
  const border = isStandard ? "var(--c-borderStrong)" : `${theme.color.accent}55`;
  return (
    <span style={{ padding: "4px 10px", borderRadius: 3, fontSize: 12, background: bg, color, border: `1px solid ${border}`, fontWeight: 500 }}>
      {label}
    </span>
  );
}

function RegulationsDisplay({ countries, usStates }: { countries: string[]; usStates: string[] }) {
  const regs = regulationsForCountries(countries, usStates);
  if (countries.length === 0) {
    return (
      <div style={{ fontSize: 12, color: theme.color.faint }}>
        Select operating countries above to see applicable regulations.
      </div>
    );
  }
  if (regs.length === 0) {
    return (
      <div style={{ fontSize: 12, color: theme.color.faint }}>
        No regulations mapped for the selected countries.
      </div>
    );
  }

  const laws      = regs.filter((r) => !CYBERSECURITY_STANDARDS.has(r));
  const standards = regs.filter((r) =>  CYBERSECURITY_STANDARDS.has(r));

  function Group({ label, items, isStandard }: { label: string; items: string[]; isStandard: boolean }) {
    if (items.length === 0) return null;
    return (
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: theme.color.faint, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
          {label}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {items.map((r) => <RegChip key={r} label={r} isStandard={isStandard} />)}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Group label="Laws & Regulations"              items={laws}      isStandard={false} />
      <Group label="Cybersecurity Standards & Frameworks" items={standards} isStandard={true}  />
    </div>
  );
}

// ── Jersey DPJL 2018 guidance section ────────────────────────────────────

// Embedded guidance sourced from jerseyoic.org/guidance (Data Protection section)
const SIX_PRINCIPLES = [
  { name: "Lawful, fair & transparent", detail: "Processing must have a valid lawful basis. Be open with individuals about how their data is used." },
  { name: "Purpose limitation", detail: "Collect data for a specified purpose. Do not use it for something incompatible with that purpose." },
  { name: "Data minimisation", detail: "Only collect and hold what is strictly necessary for your stated purpose." },
  { name: "Accuracy", detail: "Keep personal data accurate and up to date. Correct or erase inaccurate data without delay." },
  { name: "Storage limitation", detail: "Do not keep personal data longer than necessary. Define and document your retention periods." },
  { name: "Security (integrity & confidentiality)", detail: "Protect data against unauthorised access, loss, or damage using appropriate technical and organisational measures." },
];

interface TierGuide {
  fee: string;
  feeNote: string;
  ropa: string;
  dpo: string;
  obligations: string[];
}

const TIER_GUIDE: Record<string, TierGuide> = {
  sole_trader: {
    fee: "£70 current / £30 proposed 2026",
    feeNote: "Annual renewal: register before 28 Feb each year. Registration expires 31 Dec.",
    ropa: "Exempt in most cases — applies only if processing is non-occasional or involves special category / criminal offence data.",
    dpo: "Not required at this scale.",
    obligations: [
      "Register with the JDPA before processing any personal data. Failure to register is a criminal offence.",
      "Publish a privacy notice explaining what data you collect, why, on what lawful basis, and how long you keep it.",
      "Document the lawful basis for every processing activity (e.g. contract, legitimate interests, consent).",
      "Notify the JDPA of any changes to your registration within 28 days — also a criminal offence if missed.",
      "Deregister within 28 days of ceasing trading (but you may remain registered while retaining records for legal obligations).",
    ],
  },
  small: {
    fee: "£70 current / £35 proposed 2026",
    feeNote: "Annual renewal: register before 28 Feb each year. Registration expires 31 Dec.",
    ropa: "Exempt if processing is occasional and does not include special category data or criminal offence data. Review each activity individually.",
    dpo: "Not mandatory. Appoint a named Data Protection Contact to handle enquiries and breaches.",
    obligations: [
      "Register with the JDPA before processing any personal data. Failure to register is a criminal offence.",
      "Appoint a named data protection contact — make their details available to staff and individuals.",
      "Publish a privacy notice. For routine data sharing, explain who receives the data and why.",
      "Document the lawful basis for every processing activity.",
      "Put written contracts in place with any third-party processors before sharing personal data with them.",
      "Train staff on their data protection obligations.",
      "Notify the JDPA of any changes to your registration within 28 days.",
    ],
  },
  medium: {
    fee: "£90 current / £45 proposed 2026",
    feeNote: "Annual renewal: register before 28 Feb each year. Registration expires 31 Dec. Revenue surcharges may apply above £5m.",
    ropa: "Exempt for most activities under 250 employees — but review each processing type. Non-occasional processing or special category data removes the exemption for that activity.",
    dpo: "Mandatory if core activities involve large-scale systematic monitoring of individuals, or large-scale processing of special category data. Otherwise strongly recommended to appoint a Data Protection Lead.",
    obligations: [
      "Register with the JDPA before processing any personal data. Failure to register is a criminal offence.",
      "Appoint a Data Protection Lead (or DPO if mandatory). DPO must be independent, report to senior management, and cannot hold roles that determine processing purposes (e.g. CEO, IT Manager).",
      "Publish a privacy notice and review it annually.",
      "Document the lawful basis for every processing activity and keep those records current.",
      "Conduct a Data Protection Impact Assessment (DPIA) before any high-risk processing activity.",
      "Put written contracts in place with all processors and sub-processors. Review sub-processor lists regularly.",
      "Maintain written data sharing agreements for any routine disclosure of personal data to third parties.",
      "Train all staff on data protection obligations. Keep training records.",
      "Notify the JDPA of any changes to your registration within 28 days.",
    ],
  },
  large: {
    fee: "£500 current / £650 proposed 2026",
    feeNote: "Revenue surcharges apply: +£150 for £5m–£20m revenue; +£500 above £20m. Proceeds of Crime supplement may also apply. Annual renewal by 28 Feb.",
    ropa: "Mandatory if headcount reaches 250+ (Articles 6 & 22, DPJL 2018). For organisations under 250 employees, exemption may still apply for occasional, low-risk processing — but review each activity. In practice, large organisations should maintain a full ROPA.",
    dpo: "Mandatory if core activities involve large-scale systematic monitoring, large-scale special category data, or if you are a public authority. DPO must be independent, have expert knowledge of data protection law, report directly to the highest level of management, and must not hold any role that determines processing purposes.",
    obligations: [
      "Register with the JDPA before processing any personal data. Failure to register is a criminal offence.",
      "Appoint a qualified, independent DPO. Ensure they have adequate resources, access to all processing activities, and a direct line to senior management.",
      "Maintain a full Record of Processing Activities (ROPA) describing each processing type, purpose, data categories, individuals affected, retention periods, security measures, and third-party disclosures.",
      "Conduct DPIAs for all high-risk processing activities before they commence.",
      "Publish and maintain an up-to-date privacy notice. Inform individuals of any routine data sharing.",
      "Put written contracts in place with all processors and sub-processors. Approve sub-processors individually or via a general authorisation with a maintained list.",
      "Maintain written data sharing agreements for all inter-organisation data flows. Review annually.",
      "Implement appropriate technical and organisational security measures proportionate to the risk.",
      "Deliver regular staff training. Keep evidence of completion.",
      "Notify the JDPA of any changes to your registration within 28 days.",
    ],
  },
  charity: {
    fee: "Exempt from registration charges",
    feeNote: "Non-profit associations must still register with the JDPA — fee exemption does not mean registration is optional. Renewal window: Jan–Feb annually.",
    ropa: "May be exempt depending on the scale and nature of processing. Applies the same test as other organisations: processing must be occasional and must not involve special category data.",
    dpo: "Not mandatory unless you conduct large-scale systematic monitoring or large-scale special category processing. Appoint a named data protection contact as a minimum.",
    obligations: [
      "Register with the JDPA even though no fee is payable. Processing without registration is a criminal offence.",
      "The fee exemption applies to organisations established primarily for charitable, religious, cultural, educational, social or fraternal purposes under the Non-profit Organizations (Jersey) Law 2008.",
      "All six DPJL 2018 principles apply in full — the exemption covers fees only, not compliance obligations.",
      "Publish a privacy notice explaining your data use to members, donors, and beneficiaries.",
      "Document the lawful basis for every processing activity. Consent is common but not the only option.",
      "Put written contracts in place with any third-party processors.",
      "Train volunteers and staff on data protection obligations.",
      "Notify the JDPA of any changes to your registration within 28 days.",
    ],
  },
};

const BREACH_GUIDE = {
  headline: "72-hour breach notification to JOIC",
  steps: [
    "Notify the JDPA without undue delay — and within 72 hours of discovering a breach that poses risk to individuals' rights and freedoms. If you cannot provide full details in time, submit an initial notification with basic facts and follow up.",
    "Notify affected individuals directly if the breach presents high risk to their rights and freedoms (significant potential harm + meaningful likelihood). Provide: what happened, data categories involved, likely consequences, protective steps they can take, and your data protection contact details.",
    "Log every breach — regardless of severity or whether you notified the JDPA. The breach log must record: facts, impact assessment, and remedial actions. The JDPA may inspect it at any time.",
    "Fines for failure to notify can reach £5,000,000.",
  ],
};

export function JerseyDpSection({ dpTier }: { dpTier: string | null }) {
  const [showPrinciples, setShowPrinciples] = useState(false);
  const [showBreach, setShowBreach] = useState(false);
  const guide = dpTier ? TIER_GUIDE[dpTier] : null;

  const subHead: React.CSSProperties = {
    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
    color: theme.color.faint, marginBottom: 6,
  };
  const pill = (label: string, color: string): React.CSSProperties => ({
    display: "inline-block", padding: "2px 8px", borderRadius: 3,
    fontSize: 11, fontWeight: 600, background: color + "22", color,
  });

  return (
    <div style={{ borderLeft: "3px solid #1d4ed8", paddingLeft: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 6 }}>
        Jersey Data Protection (Jersey) Law 2018
      </div>

      {!dpTier ? (
        <div style={{ fontSize: 12, color: theme.color.faint }}>
          Set your DPJL 2018 tier in the Organisation section above to see tailored obligations and guidance.
        </div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 12, color: theme.color.subtle }}>Your tier:</span>
            <DpTierBadge tier={dpTier} />
          </div>

          {guide && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

              {/* Registration */}
              <div>
                <div style={subHead}>Registration</div>
                <div style={{ display: "flex", gap: 8, alignItems: "baseline", marginBottom: 4 }}>
                  <span style={pill("Annual fee", "#1d4ed8")}>Annual fee</span>
                  <span style={{ fontSize: 12, color: theme.color.heading, fontWeight: 600 }}>{guide.fee}</span>
                </div>
                <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{guide.feeNote}</div>
                <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 4, lineHeight: 1.5 }}>
                  FTE count uses the <strong>highest headcount in the preceding 12 months</strong> — vacancies excluded. Part-time: ≤9h/wk = 0.25 FTE; 10–18h = 0.5; 19–27h = 0.75; 27h+ = 1.
                </div>
              </div>

              {/* Record-keeping */}
              <div>
                <div style={subHead}>Record of Processing Activities (ROPA)</div>
                <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{guide.ropa}</div>
              </div>

              {/* DPO */}
              <div>
                <div style={subHead}>Data Protection Officer / Lead</div>
                <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{guide.dpo}</div>
              </div>

              {/* Obligations checklist */}
              <div>
                <div style={subHead}>Your obligations under the DPJL 2018</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {guide.obligations.map((o) => (
                    <li key={o} style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 5, lineHeight: 1.55 }}>{o}</li>
                  ))}
                </ul>
              </div>

              {/* Breach — collapsible */}
              <div>
                <button
                  onClick={() => setShowBreach((v) => !v)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 6 }}
                >
                  <div style={subHead}>{BREACH_GUIDE.headline}</div>
                  <span style={{ fontSize: 10, color: theme.color.faint, marginBottom: 6 }}>{showBreach ? "▲" : "▼"}</span>
                </button>
                {showBreach && (
                  <ul style={{ margin: 0, paddingLeft: 16 }}>
                    {BREACH_GUIDE.steps.map((s) => (
                      <li key={s} style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 5, lineHeight: 1.55 }}>{s}</li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Six principles — collapsible */}
              <div>
                <button
                  onClick={() => setShowPrinciples((v) => !v)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 6 }}
                >
                  <div style={subHead}>The six data protection principles</div>
                  <span style={{ fontSize: 10, color: theme.color.faint, marginBottom: 6 }}>{showPrinciples ? "▲" : "▼"}</span>
                </button>
                {showPrinciples && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {SIX_PRINCIPLES.map((p, i) => (
                      <div key={p.name} style={{ display: "flex", gap: 8 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: "#1d4ed8", flexShrink: 0, width: 16 }}>{i + 1}.</span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.heading }}>{p.name}</div>
                          <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>{p.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Further guidance links */}
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", paddingTop: 4, borderTop: `1px solid ${theme.color.border}` }}>
                {[
                  ["Registration", "https://jerseyoic.org/guidance/data-protection/registration"],
                  ["Controller duties", "https://jerseyoic.org/guidance/data-protection/data-controller-processor-duties"],
                  ["Data breaches", "https://jerseyoic.org/guidance/data-protection/data-breaches"],
                  ["Exemptions", "https://jerseyoic.org/guidance/data-protection/exemptions"],
                  ["Individual rights", "https://jerseyoic.org/guidance/data-protection/individual-rights"],
                ].map(([label, url]) => (
                  <a key={label} href={url} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 11, color: theme.color.accent, textDecoration: "underline" }}>
                    {label} →
                  </a>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Field components ──────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: theme.color.subtle,
  display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em",
};
const textareaStyle: React.CSSProperties = {
  ...inputStyle, resize: "vertical", minHeight: 72, fontFamily: "inherit",
};
const sectionTitleStyle: React.CSSProperties = {
  fontSize: 13, fontWeight: 700, color: theme.color.heading, marginBottom: 2,
};
const sectionSubStyle: React.CSSProperties = {
  fontSize: 12, color: theme.color.subtle, marginBottom: 16,
};

/** ISO 17442: 18 characters, then two check digits; the whole code read as a number is 1 mod 97. */
function leiProblem(raw: string): string | null {
  const v = raw.trim().toUpperCase();
  if (!v) return null;
  if (!/^[A-Z0-9]{18}[0-9]{2}$/.test(v)) return "An LEI is 20 letters and digits, ending in two check digits.";
  let rem = 0;
  for (const ch of v) {
    const n = ch >= "A" && ch <= "Z" ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of n) rem = (rem * 10 + Number(d)) % 97;
  }
  return rem === 1 ? null : "The check digits do not match — check the code against your LEI certificate.";
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      {children}
    </div>
  );
}

function TextField({
  label, value, placeholder, onChange, wide, multiline,
}: {
  label: string; value: string; placeholder?: string;
  onChange: (v: string) => void; wide?: boolean; multiline?: boolean;
}) {
  return (
    <div style={{ gridColumn: (wide || multiline) ? "1 / -1" : undefined }}>
      <label style={labelStyle}>{label}</label>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={textareaStyle} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
      )}
    </div>
  );
}

/**
 * A named responsibility — DPO, CISO, Quality Manager and the rest.
 *
 * These stay free text on purpose: the holder is often someone the platform has no account for
 * (an outsourced DPO, a fractional CISO, a director who never signs in), and the value is read as
 * prose by the policy and notice generators. What was wrong was typing a name the platform
 * already knows and never noticing when that person leaves — so current members are offered as
 * suggestions, and a name matching nobody is called out rather than left to go quietly stale.
 */
function PersonField({
  label, value, placeholder, members, onChange, wide,
}: {
  label: string; value: string; placeholder?: string; members: Member[];
  onChange: (v: string) => void; wide?: boolean;
}) {
  // Several of these fields render twice under different headings, so the datalist id must be
  // unique per instance rather than derived from the label.
  const listId = useId();
  const suggestions = members.map((m) => (m.jobTitle ? `${m.displayName}, ${m.jobTitle}` : m.displayName));
  // Match on the name alone: "Emma Clarke, Data Protection Officer" names Emma whether or not
  // her job title on the platform says the same thing.
  const named = value.split(",")[0].trim().toLowerCase();
  const unknownPerson = named.length > 0 && !members.some((m) => m.displayName.trim().toLowerCase() === named);

  return (
    <div style={{ gridColumn: wide ? "1 / -1" : undefined }}>
      <label style={labelStyle}>{label}</label>
      <input
        list={listId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
      />
      <datalist id={listId}>
        {suggestions.map((s) => <option key={s} value={s} />)}
      </datalist>
      {unknownPerson && members.length > 0 && (
        <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4, lineHeight: 1.4 }}>
          Not a current member of this organisation. That is fine for an outsourced or external
          appointment — but if they have left, generated policies and notices will still name them.
        </div>
      )}
    </div>
  );
}

function SelectField({
  label, value, options, onChange,
}: {
  label: string; value: string; options: string[]; onChange: (v: string) => void;
}) {
  return (
    <div>
      <label style={labelStyle}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}>
        {options.map((o) => <option key={o} value={o}>{o || "— select —"}</option>)}
      </select>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

/** GET /organizations/ai-profile: the check history later prompts are given, worked out from the checks themselves. */
interface AiProfile {
  profile: string | null;
  /** What it is worked out from: the intake questionnaire until a check completes, the checks after. */
  basis?: "checks" | "intake" | null;
  /** When the latest completed check finished. */
  lastUpdated: string | null;
}

interface AiConfig {
  aiSource: "platform" | "consultancy" | "own";
  aiProvider: "anthropic" | "openai" | "azure_openai" | null;
  aiKeyVaultUri: string | null;
  aiEndpointUrl: string | null;
  aiModel: string | null;
  aiSystemPrompt: string | null;
  hasConsultancyAi: boolean;
}

// ── Brand color extraction (canvas, no library) ───────────────────────────

function extractDominantColors(img: HTMLImageElement, count = 5): string[] {
  const canvas = document.createElement("canvas");
  const SIZE = 80;
  canvas.width = SIZE; canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return [];
  ctx.drawImage(img, 0, 0, SIZE, SIZE);
  const data = ctx.getImageData(0, 0, SIZE, SIZE).data;

  const buckets: Record<string, number> = {};
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
    if (a < 128) continue;
    const brightness = (r + g + b) / 3;
    if (brightness > 235 || brightness < 20) continue; // skip near-white/black
    const key = `${r >> 4},${g >> 4},${b >> 4}`;
    buckets[key] = (buckets[key] ?? 0) + 1;
  }

  const sorted = Object.entries(buckets).sort((a, b) => b[1] - a[1]);
  const result: string[] = [];
  for (const [key] of sorted) {
    const [rq, gq, bq] = key.split(",").map(Number);
    const r = rq * 16 + 8, g = gq * 16 + 8, b = bq * 16 + 8;
    const hex = `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    const tooClose = result.some((existing) => {
      const er = parseInt(existing.slice(1, 3), 16);
      const eg = parseInt(existing.slice(3, 5), 16);
      const eb = parseInt(existing.slice(5, 7), 16);
      return Math.abs(er - r) < 48 && Math.abs(eg - g) < 48 && Math.abs(eb - b) < 48;
    });
    if (!tooClose) result.push(hex);
    if (result.length >= count) break;
  }
  return result;
}

export function OrgProfilePage() {
  const { enrolled, reload: reloadFrameworks } = useFrameworks();
  // Every write here needs org:admin (organizations.routes.ts); anyone else reads the page.
  const { user } = useCurrentUser();
  const readOnly = !!user && !user.permissions?.includes("org:admin");
  const [org, setOrg] = useState<Organization | null>(null);
  const [countries, setCountries] = useState<string[]>([]);
  const [siteCountries, setSiteCountries] = useState<string[]>([]);
  const [usStates, setUsStates] = useState<string[]>([]);
  const [siteUsStates, setSiteUsStates] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  // The DPJL tier is sent only once someone picks one: echoing back the stored (derived) tier
  // would pin it, and a headcount change would never re-derive it.
  const [dpTierTouched, setDpTierTouched] = useState(false);
  const [aiProfile, setAiProfile] = useState<AiProfile | null>(null);
  const [aiProfileOpen, setAiProfileOpen] = useState(false);
  const [aiConfig, setAiConfig] = useState<AiConfig | null>(null);
  const [aiConfigSaving, setAiConfigSaving] = useState(false);
  const [aiConfigSaved, setAiConfigSaved] = useState(false);
  const [aiConfigError, setAiConfigError] = useState("");

  // Current members, to suggest names for the responsibility fields.
  const [members, setMembers] = useState<Member[]>([]);

  // Logo / brand identity
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [extractedColors, setExtractedColors] = useState<string[]>([]);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.get<{ organization: Organization }>("/organizations/profile").then((d) => {
      const o = d.organization;
      if (o.effectiveDate) o.effectiveDate = o.effectiveDate.slice(0, 10);
      if (o.dpoNotifiedToAuthorityAt) o.dpoNotifiedToAuthorityAt = o.dpoNotifiedToAuthorityAt.slice(0, 10);
      setOrg(o);
      setCountries(parseJsonArray(o.geography));
      // US states in scope ride beside the profile fields (the index signature keeps them out of Organization).
      setUsStates((d.organization as unknown as { usStates?: string[] }).usStates ?? []);
      // Fetch a short-lived view URL if a logo is stored
      if (o.logoBlobPath) {
        api.get<{ url: string | null }>("/organizations/logo-url")
          .then((r) => { if (r.url) setLogoPreviewUrl(r.url); })
          .catch(() => {});
      }
    });
    api.get<AiProfile>("/organizations/ai-profile").then(setAiProfile).catch(() => {});
    api.get<AiConfig>("/organizations/ai-config").then(setAiConfig).catch(() => {});
    api.get<{ summary: { countries: string[]; usStates?: string[] } }>("/sites").then((d) => {
      setSiteCountries(d.summary.countries);
      setSiteUsStates(d.summary.usStates ?? []);
    }).catch(() => {});
    // Suggestions for the responsibility fields below. A failure here costs nothing: the fields
    // stay plain text, which is what they were.
    api.get<{ members: Member[] }>("/organizations/members")
      .then((d) => setMembers(d.members ?? []))
      .catch(() => {});
  }, []);

  function setField(key: keyof Organization, value: string | boolean | null) {
    setOrg((prev) => (prev ? { ...prev, [key]: value } : prev));
    setSaved(false);
    setError("");
  }

  async function handleLogoFile(file: File) {
    if (!file.type.match(/^image\/(png|jpeg|webp)$/)) {
      setLogoError("Please upload a PNG, JPEG, or WebP image.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setLogoError("Logo must be under 2 MB.");
      return;
    }
    setLogoError("");
    setLogoUploading(true);
    try {
      // Step 1 — request SAS upload URL
      const { uploadUrl, blobPath } = await api.post<{ uploadUrl: string; blobPath: string }>(
        "/organizations/logo/request-upload",
        { contentType: file.type }
      );
      // Step 2 — PUT directly to Azure Blob Storage
      await fetch(uploadUrl, { method: "PUT", body: file, headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type } });
      // Step 3 — show preview and extract colors
      const objectUrl = URL.createObjectURL(file);
      setLogoPreviewUrl(objectUrl);
      const img = new Image();
      img.onload = () => {
        const colors = extractDominantColors(img);
        setExtractedColors(colors);
        // Pre-fill color fields if not already set
        setOrg((prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            logoBlobPath: blobPath,
            primaryBrandColor: prev.primaryBrandColor ?? (colors[0] ?? null),
            accentBrandColor:  prev.accentBrandColor  ?? (colors[1] ?? null),
          };
        });
      };
      img.src = objectUrl;
    } catch {
      setLogoError("Upload failed. Please try again.");
    } finally {
      setLogoUploading(false);
    }
  }

  async function handleRemoveLogo() {
    try {
      await api.del("/organizations/logo");
      setLogoPreviewUrl(null);
      setExtractedColors([]);
      setOrg((prev) => prev ? { ...prev, logoBlobPath: null, primaryBrandColor: null, accentBrandColor: null, useBrandColorsInPolicies: false } : prev);
    } catch {
      setLogoError("Failed to remove logo.");
    }
  }

  async function handleSave() {
    if (!org) return;
    setSaving(true); setSaved(false); setError("");
    const leiError = leiProblem(org.lei ?? "");
    if (leiError) { setError(`Legal Entity Identifier: ${leiError}`); setSaving(false); return; }
    // A part-typed colour would fail the whole save: say which box instead.
    for (const [label, value] of [["Primary colour", org.primaryBrandColor], ["Accent colour", org.accentBrandColor]] as const) {
      if (value && !/^#[0-9a-fA-F]{6}$/.test(value)) { setError(`${label} must be six hex digits, such as #1E3A5F. Finish it or clear the box.`); setSaving(false); return; }
    }
    if (!org.name.trim()) { setError("Organisation name is required."); setSaving(false); return; }
    try {
      const { id, dpTier, ...rest } = org;
      void id;
      const d = await api.patch<{ organization: Organization }>("/organizations/profile", {
        ...rest,
        ...(dpTierTouched ? { dpTier: dpTier || null } : {}),
        geography: JSON.stringify(countries),
        usStates,
        // regulations is auto-computed from geography and US states by the backend
      });
      // The server decides the DPJL tier (derived, or cleared when Jersey leaves); show what it kept.
      setOrg((prev) => (prev ? { ...prev, dpTier: d.organization.dpTier ?? null } : prev));
      setDpTierTouched(false);
      setSaved(true);
      reloadFrameworks();
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to save. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  async function handleAiConfigSave() {
    if (!aiConfig) return;
    setAiConfigSaving(true); setAiConfigSaved(false); setAiConfigError("");
    try {
      await api.patch("/organizations/ai-config", aiConfig);
      setAiConfigSaved(true);
    } catch {
      setAiConfigError("Failed to save AI config. Please try again.");
    } finally {
      setAiConfigSaving(false);
    }
  }

  function setAiConfigField<K extends keyof AiConfig>(key: K, value: AiConfig[K]) {
    setAiConfig((prev) => prev ? { ...prev, [key]: value } : prev);
    setAiConfigSaved(false);
    setAiConfigError("");
  }

  if (!org) return <LoadingBlock />;

  const val = (k: keyof Organization) => String(org[k] ?? "");

  const has27001   = enrolled.has("iso27001");
  const has42001   = enrolled.has("iso42001");
  const has9001    = enrolled.has("iso9001");
  const hasSoc2    = enrolled.has("soc2");
  const hasCe      = enrolled.has("cyber_essentials") || enrolled.has("cyber_essentials_plus");
  const anyFramework = has27001 || has42001 || has9001 || hasSoc2 || hasCe;

  const conditionalFields: Array<keyof Organization> = [];
  if (has27001 || hasCe || hasSoc2) conditionalFields.push("ciso");
  if (has27001 || has42001)         conditionalFields.push("dpo");
  if (has27001)                     conditionalFields.push("ismsScope", "riskAppetite", "reviewFrequency", "effectiveDate");
  if (has42001)                     conditionalFields.push("aimsScope", "chiefAiOfficer");
  if (has9001)                      conditionalFields.push("qmsScope", "qualityManager");
  if (hasSoc2)                      conditionalFields.push("socServiceDescription");
  if (has27001 || hasSoc2)          conditionalFields.push("siem");
  if (has27001 || hasCe || hasSoc2) conditionalFields.push("vulnScanner");

  const completionFields: Array<keyof Organization> = [
    "name", "sector", "size",
    "ceo", "itManager", "hrLead",
    "infrastructure", "cloudProviders",
    ...conditionalFields,
  ];

  const filledCount = completionFields.filter((f) => !!org[f]).length;
  const pct = Math.round((filledCount / completionFields.length) * 100);
  const barColor = pct === 100 ? theme.color.success : pct >= 60 ? theme.color.accent : theme.color.warning;

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        My Organisation
      </h1>
      <p style={{ margin: "0 0 24px", fontSize: 13, color: theme.color.subtle }}>
        These details personalise your policy templates, reports, and AI compliance guidance.
        Framework-specific sections appear based on your enrolled programmes.
      </p>

      {/* Profile completion */}
      <div style={{ ...card, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>Profile completion</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: barColor }}>{pct}%</span>
        </div>
        <RulerProgress value={pct} fill={barColor} label="Profile completion" />
        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 6 }}>
          {filledCount} of {completionFields.length} fields completed
        </div>
      </div>

      {readOnly && (
        <div style={{ ...card, marginBottom: 16, fontSize: 12, color: theme.color.subtle, background: theme.color.panelAlt }}>
          You can read these details; only an organisation administrator can change them.
        </div>
      )}

      {/* Read-only for anyone without org:admin: every field and button below is disabled together. */}
      <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {/* ── Organisation ── */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={sectionTitleStyle}>Organisation</div>
        <div style={{ marginBottom: 16 }} />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          <TextField label="Organisation name" value={val("name")} placeholder="e.g. Acme Limited" onChange={(v) => setField("name", v)} wide />
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>
              Regulated sector
            </div>
            <select
              value={org.sectorCode ?? ""}
              onChange={(e) => setField("sectorCode", e.target.value)}
              style={{ ...inputStyle, width: "100%" }}
            >
              <option value="">— not set —</option>
              {CI_SECTOR_PARENTS.map((parent) => {
                const children = subSectors(parent.id);
                return children.length === 0 ? (
                  <option key={parent.id} value={parent.id}>{parent.label}</option>
                ) : (
                  <optgroup key={parent.id} label={parent.label}>
                    <option value={parent.id}>{parent.label} (all)</option>
                    {children.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </optgroup>
                );
              })}
            </select>
            <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
              {org.sectorCode
                ? CI_SECTORS.find((s) => s.id === org.sectorCode)?.description
                : "Decides which cyber-security laws designate you. Leave unset and your sector description below is matched instead, which is less reliable."}
            </div>
          </div>
          <TextField label="Sector / industry" value={val("sector")} placeholder="e.g. Financial Technology" onChange={(v) => setField("sector", v)} />
          <TextField label="Headcount (employees)" value={val("size")} placeholder="e.g. 85" onChange={(v) => setField("size", v)} />
          {(org.doraMonitoringEnabled || !!org.lei) && (
            <div>
              <TextField label="Legal Entity Identifier (LEI)" value={val("lei")} placeholder="20 characters, e.g. 213800ABCDEFGHIJKL12" onChange={(v) => setField("lei", v.toUpperCase())} />
              <div style={{ fontSize: 11, color: leiProblem(val("lei")) ? theme.color.dangerText : theme.color.faint, marginTop: 4 }}>
                {leiProblem(val("lei")) ?? "Identifies the organisation in the DORA Register of Information (RT.01.01). Issued and renewed yearly by an accredited LEI issuer."}
              </div>
            </div>
          )}
          {/* The DPJL tier belongs to Jersey's data protection law alone: shown only while Jersey is an operating location. */}
          {countries.includes("Jersey") && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: theme.color.subtle, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 5 }}>
              DPJL 2018 Tier
            </div>
            <select
              value={org.dpTier ?? ""}
              onChange={(e) => { setField("dpTier", e.target.value); setDpTierTouched(true); }}
              style={{ ...inputStyle, width: "100%" }}
            >
              <option value="">— auto-derive from headcount —</option>
              {DP_TIERS.map((t) => (
                <option key={t.value} value={t.value}>{t.label} ({t.description})</option>
              ))}
            </select>
            {org.dpTier === "charity" && (
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
                Charity / non-profit — exempt from JDPA registration fees.
              </div>
            )}
            {!org.dpTier && org.size && (
              <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
                Will be derived from headcount on save.
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* ── Location ── */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={sectionTitleStyle}>Operating Locations</div>
        <div style={sectionSubStyle}>
          Select the countries where the organisation is established: offices, registered entities or staff. Each country's laws are derived from your selection. Countries whose residents you only sell to or monitor, and countries where you only host data, are recorded on the data protection thresholds of the Legal obligations page: the first bring in their data protection laws only, the second no laws at all.
          {siteCountries.length > 0 && " Countries with a site (Sites & locations) are included automatically and stay until the site is removed or moved."}
        </div>
        <Field label="Countries">
          <CountryPicker selected={countries} locked={siteCountries} onChange={(v) => { setCountries(v); setSaved(false); }} />
        </Field>
        {countries.includes("United States") && (
          <Field label="US states">
            <div style={{ ...sectionSubStyle, marginTop: 0 }}>
              The states whose residents you serve or whose residents&apos; personal data you hold — that, not where your offices are,
              is what brings a state&apos;s breach-notification and privacy laws into scope. Federal law applies whichever you pick.
              States with a US site are included automatically. The United States is still billed as one jurisdiction.
            </div>
            <CountryPicker
              selected={usStates}
              locked={siteUsStates}
              options={US_STATES}
              placeholder="Search and add states…"
              onChange={(v) => { setUsStates(v); setSaved(false); }}
            />
          </Field>
        )}
      </div>

      {/* ── Regulations (read-only — auto-derived from geography on save) ── */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={sectionTitleStyle}>Applicable Regulations</div>
        <div style={sectionSubStyle}>
          Automatically derived from your operating locations. Regulations are updated whenever you save your organisation profile.
          Access to jurisdiction law and guidance is included in your subscription at £49 per operating country per month.
        </div>
        <RegulationsDisplay countries={countries} usStates={usStates} />
      </div>

      {/* ── Framework-specific scope sections ── */}
      {!anyFramework && (
        <div style={{ ...card, marginBottom: 16, borderLeft: `3px solid ${theme.color.accent}` }}>
          <div style={{ fontSize: 13, color: theme.color.subtle }}>
            Enrol in a compliance programme via <strong>My Services</strong> to unlock framework-specific profile fields (scope statements, management system objectives, and role assignments).
          </div>
        </div>
      )}

      {has27001 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={sectionTitleStyle}>ISO 27001 — ISMS</div>
          <div style={sectionSubStyle}>Used in policy templates, Statement of Applicability, and AI guidance for your information security management system.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>ISMS scope</label>
              <textarea
                value={val("ismsScope")}
                onChange={(e) => setField("ismsScope", e.target.value)}
                placeholder="e.g. All cloud infrastructure, corporate systems, and staff at our Jersey headquarters. Excludes personal devices."
                style={textareaStyle}
              />
            </div>
            <SelectField label="Risk appetite" value={val("riskAppetite")} options={["", "Low", "Medium", "High"]} onChange={(v) => setField("riskAppetite", v)} />
            <SelectField label="Review frequency" value={val("reviewFrequency")} options={["", "Annual", "Bi-annual", "Quarterly", "Monthly"]} onChange={(v) => setField("reviewFrequency", v)} />
            <div>
              <label style={labelStyle}>ISMS effective date</label>
              <input type="date" value={val("effectiveDate")} onChange={(e) => setField("effectiveDate", e.target.value)} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
            </div>
          </div>
        </div>
      )}

      {has42001 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={sectionTitleStyle}>ISO 42001 — AIMS</div>
          <div style={sectionSubStyle}>Used in AI management system documentation, impact assessments, and AI compliance guidance.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>AIMS scope</label>
              <textarea
                value={val("aimsScope")}
                onChange={(e) => setField("aimsScope", e.target.value)}
                placeholder="e.g. All AI systems designed, deployed, or used by the organisation, including third-party AI platforms integrated into operational processes."
                style={textareaStyle}
              />
            </div>
            <PersonField members={members} label="AI governance owner / Chief AI Officer" value={val("chiefAiOfficer")} placeholder="e.g. Dr. Sarah Mitchell, Chief AI Officer" onChange={(v) => setField("chiefAiOfficer", v)} wide />
          </div>
        </div>
      )}

      {has9001 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={sectionTitleStyle}>ISO 9001 — QMS</div>
          <div style={sectionSubStyle}>Used in quality management system documentation, policy templates, and compliance guidance.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>QMS scope</label>
              <textarea
                value={val("qmsScope")}
                onChange={(e) => setField("qmsScope", e.target.value)}
                placeholder="e.g. Design, development, and delivery of software products and professional services to enterprise clients."
                style={textareaStyle}
              />
            </div>
            <PersonField members={members} label="Quality Manager" value={val("qualityManager")} placeholder="e.g. James Hartley, Quality Manager" onChange={(v) => setField("qualityManager", v)} wide />
          </div>
        </div>
      )}

      {hasSoc2 && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={sectionTitleStyle}>SOC 2</div>
          <div style={sectionSubStyle}>Used in the SOC 2 report scope, system description, and Trust Services Criteria evidence.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>Service description (system in scope)</label>
              <textarea
                value={val("socServiceDescription")}
                onChange={(e) => setField("socServiceDescription", e.target.value)}
                placeholder="e.g. Cloud-based compliance management platform providing document storage, AI-driven compliance checks, and reporting to enterprise clients."
                style={textareaStyle}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Key Personnel ── */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={sectionTitleStyle}>Key Personnel</div>
        <div style={sectionSubStyle}>Named in policy documents and compliance records as owners, approvers, and responsible parties.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          <PersonField members={members} label="CEO" value={val("ceo")} placeholder="e.g. Sarah Mitchell, CEO" onChange={(v) => setField("ceo", v)} />
          {(has27001 || hasCe || hasSoc2) && (
            <PersonField members={members} label="CISO / Security lead" value={val("ciso")} placeholder="e.g. James Hartley, CISO" onChange={(v) => setField("ciso", v)} />
          )}
          {(has27001 || has42001) && (
            <PersonField members={members} label="Data Protection Officer (DPO)" value={val("dpo")} placeholder="e.g. Emma Clarke, DPO" onChange={(v) => setField("dpo", v)} />
          )}
          {val("dpo") && (
            <div>
              <label style={{ ...labelStyle }}>DPO contact details given to the supervisory authority on</label>
              <input type="date" value={val("dpoNotifiedToAuthorityAt")} onChange={(e) => setField("dpoNotifiedToAuthorityAt", e.target.value)}
                style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} />
              <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 4 }}>GDPR / UK GDPR Art. 37(7): the DPO's contact details are published and communicated to the authority.</div>
            </div>
          )}
          <PersonField members={members} label="IT Manager" value={val("itManager")} placeholder="e.g. Tom Webb, Head of IT" onChange={(v) => setField("itManager", v)} />
          <PersonField members={members} label="HR Lead" value={val("hrLead")} placeholder="e.g. Rachel Stone, HR Manager" onChange={(v) => setField("hrLead", v)} />
          {has42001 && !val("chiefAiOfficer") && (
            <PersonField members={members} label="Chief AI Officer / AI Governance Lead" value={val("chiefAiOfficer")} placeholder="e.g. Dr. Sarah Mitchell, Chief AI Officer" onChange={(v) => setField("chiefAiOfficer", v)} />
          )}
          {has9001 && !val("qualityManager") && (
            <PersonField members={members} label="Quality Manager" value={val("qualityManager")} placeholder="e.g. James Hartley, Quality Manager" onChange={(v) => setField("qualityManager", v)} />
          )}
        </div>
      </div>

      {/* ── Technical Environment ── */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={sectionTitleStyle}>Technical Environment</div>
        <div style={sectionSubStyle}>Referenced in technical controls, AI compliance checks, and evidence reviews.</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          <SelectField label="Infrastructure type" value={val("infrastructure")} options={["", "Cloud-first", "On-premises", "Hybrid", "Multi-cloud"]} onChange={(v) => setField("infrastructure", v)} />
          <TextField label="Cloud providers" value={val("cloudProviders")} placeholder="e.g. AWS, Microsoft Azure, Google Cloud" onChange={(v) => setField("cloudProviders", v)} />
          <TextField label="Core applications" value={val("coreApps")} placeholder="e.g. Microsoft 365, Salesforce, Jira" onChange={(v) => setField("coreApps", v)} />
          <TextField label="Identity provider (IdP)" value={val("idp")} placeholder="e.g. Microsoft Entra ID" onChange={(v) => setField("idp", v)} />
          {(has27001 || hasSoc2) && (
            <TextField label="SIEM" value={val("siem")} placeholder="e.g. Microsoft Sentinel" onChange={(v) => setField("siem", v)} />
          )}
          {(has27001 || hasCe || hasSoc2) && (
            <TextField label="Vulnerability scanner" value={val("vulnScanner")} placeholder="e.g. Tenable.io" onChange={(v) => setField("vulnScanner", v)} />
          )}
          <TextField label="Backup solution" value={val("backup")} placeholder="e.g. AWS Backup (daily, 30-day retention)" onChange={(v) => setField("backup", v)} />
        </div>
      </div>

      {/* ── Brand Identity ── */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={sectionTitleStyle}>Brand Identity</div>
        <div style={sectionSubStyle}>
          Upload your logo and set brand colours. When enabled, generated policy documents will include your logo and use your colours for headings.
        </div>

        {/* Logo upload area */}
        <div style={{ marginBottom: 20 }}>
          <label style={labelStyle}>Company logo</label>
          <div
            style={{
              display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap",
            }}
          >
            {/* Preview / drop target */}
            <div
              onClick={() => !logoUploading && logoInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) handleLogoFile(file);
              }}
              style={{
                width: 160, height: 80, border: `2px dashed ${theme.color.border}`,
                borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center",
                cursor: logoUploading ? "default" : "pointer", overflow: "hidden",
                background: theme.color.panelAlt ?? theme.color.panel, position: "relative",
                flexShrink: 0,
              }}
            >
              {logoPreviewUrl ? (
                <img
                  src={logoPreviewUrl}
                  alt="Logo preview"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              ) : (
                <span style={{ fontSize: 11, color: theme.color.faint, textAlign: "center", padding: "0 8px", lineHeight: 1.5 }}>
                  {logoUploading ? "Uploading…" : "Click or drop\nPNG / JPEG / WebP"}
                </span>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                style={{
                  padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 3,
                  border: `1px solid ${theme.color.border}`, background: "transparent",
                  color: theme.color.text, cursor: logoUploading ? "default" : "pointer",
                  opacity: logoUploading ? 0.6 : 1,
                }}
              >
                {logoPreviewUrl ? "Replace logo" : "Upload logo"}
              </button>
              {logoPreviewUrl && (
                <button
                  onClick={handleRemoveLogo}
                  style={{
                    padding: "6px 14px", fontSize: 12, fontWeight: 600, borderRadius: 3,
                    border: `1px solid ${theme.color.dangerText ?? "#dc2626"}`,
                    background: "transparent", color: theme.color.dangerText ?? cbSafe("#dc2626"),
                    cursor: "pointer",
                  }}
                >
                  Remove logo
                </button>
              )}
              <div style={{ fontSize: 11, color: theme.color.faint }}>
                PNG, JPEG or WebP · max 2 MB
              </div>
              {logoError && (
                <div style={{ fontSize: 12, color: theme.color.dangerText ?? cbSafe("#dc2626") }}>{logoError}</div>
              )}
            </div>
          </div>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: "none" }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleLogoFile(f); e.target.value = ""; }}
          />
        </div>

        {/* Extracted color swatches */}
        {extractedColors.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Colors extracted from logo</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {extractedColors.map((hex) => (
                <button
                  key={hex}
                  title={hex}
                  onClick={() => {
                    if (!org?.primaryBrandColor || org.primaryBrandColor === hex) return;
                    if (!org?.accentBrandColor) {
                      setField("accentBrandColor", hex);
                    } else {
                      setField("primaryBrandColor", hex);
                    }
                  }}
                  style={{
                    width: 32, height: 32, borderRadius: 3, background: hex,
                    border: `2px solid ${hex === org?.primaryBrandColor || hex === org?.accentBrandColor ? theme.color.heading : theme.color.border}`,
                    cursor: "pointer", flexShrink: 0,
                  }}
                />
              ))}
              <div style={{ fontSize: 11, color: theme.color.faint, alignSelf: "center" }}>
                Click a swatch to apply it as a brand colour
              </div>
            </div>
          </div>
        )}

        {/* Color pickers */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px", marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Primary colour (headings)</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(org.primaryBrandColor ?? "") ? org.primaryBrandColor! : "#1E3A5F"}
                onChange={(e) => setField("primaryBrandColor", e.target.value)}
                style={{ width: 36, height: 36, padding: 2, border: `1px solid ${theme.color.border}`, borderRadius: 3, cursor: "pointer", background: "none" }}
              />
              <input
                type="text"
                value={org.primaryBrandColor ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  // Emptying the box returns to "no colour".
                  if (/^(#[0-9a-fA-F]{0,6})?$/.test(v)) setField("primaryBrandColor", v === "" || v === "#" ? null : v);
                }}
                placeholder="#1E3A5F"
                style={{ ...inputStyle, width: 100, fontFamily: "monospace" }}
              />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Accent colour (subheadings)</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(org.accentBrandColor ?? "") ? org.accentBrandColor! : "#2563EB"}
                onChange={(e) => setField("accentBrandColor", e.target.value)}
                style={{ width: 36, height: 36, padding: 2, border: `1px solid ${theme.color.border}`, borderRadius: 3, cursor: "pointer", background: "none" }}
              />
              <input
                type="text"
                value={org.accentBrandColor ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  // Emptying the box returns to "no colour".
                  if (/^(#[0-9a-fA-F]{0,6})?$/.test(v)) setField("accentBrandColor", v === "" || v === "#" ? null : v);
                }}
                placeholder="#2563EB"
                style={{ ...inputStyle, width: 100, fontFamily: "monospace" }}
              />
            </div>
          </div>
        </div>

        {/* Color preview strip */}
        {(org.primaryBrandColor || org.accentBrandColor) && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {org.primaryBrandColor && /^#[0-9a-fA-F]{6}$/.test(org.primaryBrandColor) && (
              <div style={{ flex: 1, height: 8, borderRadius: 4, background: org.primaryBrandColor }} />
            )}
            {org.accentBrandColor && /^#[0-9a-fA-F]{6}$/.test(org.accentBrandColor) && (
              <div style={{ flex: 1, height: 8, borderRadius: 4, background: org.accentBrandColor }} />
            )}
          </div>
        )}

        {/* Use in policies toggle */}
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" }}>
          <input
            type="checkbox"
            checked={!!org.useBrandColorsInPolicies}
            onChange={(e) => setField("useBrandColorsInPolicies", e.target.checked)}
            style={{ width: 16, height: 16, cursor: "pointer" }}
          />
          <span style={{ fontSize: 13, color: theme.color.text }}>
            Apply logo and brand colours to generated policy documents
          </span>
        </label>
        {org.useBrandColorsInPolicies && !org.logoBlobPath && (
          <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 6, marginLeft: 26 }}>
            Upload a logo above to include it in policies. Colors will still apply without a logo.
          </div>
        )}
      </div>

      {/* ── Document template (client Word template) ── */}
      <DocumentTemplatePanel />

      {/* ── Consultant access (may the consultant generate documents) ── */}
      <ConsultantDocGenPanel />

      {/* Save */}
      {!readOnly && <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
        <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
          {saving ? "Saving…" : "Save changes"}
        </button>
        {saved && <span style={{ fontSize: 12, color: theme.color.success }}>Saved</span>}
        {error && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>}
      </div>}

      {/* ── AI Provider ── */}
      {aiConfig && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={sectionTitleStyle}>AI Provider</div>
          <div style={{ ...sectionSubStyle, marginBottom: 20 }}>
            Choose which AI powers your compliance checks, policy generation and guidance. You can choose the model for your compliance checks on the platform AI or on your own key; your consultancy chooses for the clients that use its AI.
          </div>

          {/* Source selector */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
            {(["platform", "consultancy", "own"] as const).map((src) => {
              if (src === "consultancy" && !aiConfig.hasConsultancyAi) return null;
              const labels: Record<string, string> = {
                platform: "Abacas Platform AI",
                consultancy: "My Consultant's AI",
                own: "My Own API Key (BYOK)",
              };
              const descs: Record<string, string> = {
                platform: "Abacas runs the AI. Keep the recommended model (Claude Sonnet 5.5) for compliance checks or choose another below; usage is billed to your account at the model’s rate.",
                consultancy: "Use the provider and model your consultancy has chosen. No AI cost on your account.",
                own: "Connect your own Anthropic, OpenAI or Azure OpenAI key and choose the model. You pay your provider directly.",
              };
              const selected = aiConfig.aiSource === src;
              return (
                <label key={src} style={{
                  display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
                  padding: "10px 12px", borderRadius: 3,
                  border: `1px solid ${selected ? theme.color.accent : theme.color.border}`,
                  background: selected ? theme.color.accentBg : theme.color.panel,
                }}>
                  <input
                    type="radio" name="aiSource" value={src}
                    checked={selected}
                    onChange={() => {
                      // A model chosen for one source never carries over to another.
                      setAiConfigField("aiSource", src);
                      setAiConfigField("aiModel", null);
                    }}
                    style={{ marginTop: 2, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.color.heading }}>{labels[src]}</div>
                    <div style={{ fontSize: 12, color: theme.color.subtle, marginTop: 2 }}>{descs[src]}</div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Model for checks on the platform AI */}
          {aiConfig.aiSource === "platform" && (
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Model for compliance checks</label>
              <ModelPicker
                mode="platform"
                provider="anthropic"
                value={aiConfig.aiModel}
                onChange={(m) => setAiConfigField("aiModel", m)}
              />
            </div>
          )}

          {/* BYOK fields */}
          {aiConfig.aiSource === "own" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px", marginBottom: 20 }}>
              <div>
                <label style={labelStyle}>Provider</label>
                <select
                  value={aiConfig.aiProvider ?? ""}
                  onChange={(e) => {
                    // A model belongs to its provider: switching provider clears it.
                    setAiConfigField("aiProvider", (e.target.value || null) as AiConfig["aiProvider"]);
                    setAiConfigField("aiModel", null);
                  }}
                  style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                >
                  <option value="">— select provider —</option>
                  <option value="anthropic">Anthropic (Claude)</option>
                  <option value="openai">OpenAI (GPT)</option>
                  <option value="azure_openai">Azure OpenAI</option>
                </select>
              </div>

              {aiConfig.aiProvider && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>{aiConfig.aiProvider === "azure_openai" ? "Deployment" : "Model"}</label>
                  <ModelPicker
                    key={aiConfig.aiProvider}
                    provider={aiConfig.aiProvider}
                    value={aiConfig.aiModel}
                    onChange={(m) => setAiConfigField("aiModel", m)}
                  />
                </div>
              )}

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Azure Key Vault secret URI</label>
                <input
                  value={aiConfig.aiKeyVaultUri ?? ""}
                  onChange={(e) => setAiConfigField("aiKeyVaultUri", e.target.value || null)}
                  placeholder="https://your-vault.vault.azure.net/secrets/ai-api-key"
                  style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                />
                <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 4 }}>
                  Store your API key in Azure Key Vault and paste the secret URI here. Abacas reads it at runtime using Managed Identity — the key value is never stored.
                </div>
              </div>

              {aiConfig.aiProvider === "azure_openai" && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={labelStyle}>Azure OpenAI deployment base URL</label>
                  <input
                    value={aiConfig.aiEndpointUrl ?? ""}
                    onChange={(e) => setAiConfigField("aiEndpointUrl", e.target.value || null)}
                    placeholder="https://your-resource.openai.azure.com"
                    style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
                  />
                </div>
              )}

              <div style={{ gridColumn: "1 / -1" }}>
                <label style={labelStyle}>Custom system prompt <span style={{ fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                <textarea
                  value={aiConfig.aiSystemPrompt ?? ""}
                  onChange={(e) => setAiConfigField("aiSystemPrompt", e.target.value || null)}
                  placeholder="Optional instructions prepended to every AI call for this organisation…"
                  style={{ ...textareaStyle, minHeight: 80, width: "100%", boxSizing: "border-box" }}
                />
              </div>
            </div>
          )}

          {!readOnly && <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={handleAiConfigSave} disabled={aiConfigSaving} style={{ ...btnPrimary, opacity: aiConfigSaving ? 0.7 : 1 }}>
              {aiConfigSaving ? "Saving…" : "Save AI settings"}
            </button>
            {aiConfigSaved && <span style={{ fontSize: 12, color: theme.color.success }}>Saved</span>}
            {aiConfigError && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{aiConfigError}</span>}
          </div>}
        </div>
      )}
      </fieldset>

      {/* ── AI learning profile: the check history later prompts are given ── */}
      <div style={{ ...card, marginBottom: 16 }}>
        <button
          onClick={() => setAiProfileOpen((v) => !v)}
          style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            width: "100%", background: "none", border: "none", cursor: "pointer",
            padding: 0, color: theme.color.heading,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>AI learning profile</span>
            <span style={{ fontSize: 11, color: theme.color.subtle }}>
              {aiProfile?.lastUpdated
                ? `Last check ${fmtDateTime(aiProfile.lastUpdated)}`
                : aiProfile?.basis === "intake" ? "From the intake questionnaire" : "Nothing recorded yet"}
            </span>
          </div>
          <span style={{ fontSize: 13, color: theme.color.subtle }}>{aiProfileOpen ? "▲" : "▼"}</span>
        </button>

        {aiProfileOpen && (
          <div style={{ marginTop: 14 }}>
            <p style={{ margin: "0 0 10px", fontSize: 12, color: theme.color.subtle, lineHeight: 1.5 }}>
              What later AI prompts are told about where your compliance stands. Until a compliance check has
              completed, it is how much of the intake questionnaire you have answered and how many control statuses
              were inferred from your answers. After that, it is each check&apos;s date and score, how verdicts moved
              between checks, the gaps that have persisted, where your people raised or lowered statuses after a
              check, and how your intake answers compared with the latest check. Guidance, risk and policy writing
              also see your certification auditor&apos;s findings, the evidence on file and your corrective actions.
              It is worked out from your records whenever it is used, so it never carries your notes, file names or
              the AI&apos;s own wording, and the AI is told it is background, not evidence.
            </p>

            {aiProfile?.profile ? (
              <pre style={{
                margin: 0, padding: "12px 14px",
                background: theme.color.panel, border: `1px solid ${theme.color.border}`,
                borderRadius: 3, fontSize: 11, lineHeight: 1.7,
                color: theme.color.text, overflowX: "auto", whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}>
                {aiProfile.profile}
              </pre>
            ) : (
              <div style={{ fontSize: 13, color: theme.color.subtle, padding: "10px 0" }}>
                It appears once you have answered intake questions or a compliance check has completed.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
